import {
  createBrowserRouter,
  Navigate,
  Route,
  useLocation,
  ScrollRestoration,
} from 'react-router-dom'
import { createRoutesFromElements, RouterProvider } from 'react-router'
import { PatchedTelemetryRoutes } from '@workwhile/telemetry/react'
import {
  SignOutPage,
  OnboardingPage,
  LoginPage,
  ResetPasswordPage,
} from './auth'
import { DeprecatedLayout, TopBanner } from 'pages/layout'
import { Shell } from 'components/Shell'
import { HomePage } from 'pages/HomePage'
import { Intercom } from 'components/Intercom'
import { AuthGuard } from 'components/auth'
import { ShiftEditorPage } from 'pages/ShiftEditorPage'
import NotFound from 'components/not_found/not_found'
import { ModalRoutes } from './ModalRoutes'
import { LayoutProvider } from './layout/LayoutProvider'
import { ShiftDetail } from './ShiftDetailPage'
import { WorkersPage } from './WorkersPage'
import { WorkerProfilePage } from './WorkerProfilePage'
import { ListDetailPage } from './ListDetailPage'
import { MessagePage } from './MessagePage'
import { PaymentPage } from './PaymentPage'
import { ReviewPage } from './ReviewPage'
import { InvoiceDetailPage } from './InvoiceDetailPage'
import { UpcomingInvoiceDetailPage } from './UpcomingInvoiceDetailPage'
import { AnalyticsPage } from './AnalyticsPage'
import { SettingsPage } from './settings'
import { VerifyMFA } from './auth/VerifyMFA'
import { WorkerShiftsPage } from './WorkerShiftsPage'
import OktaLoginCallback from './auth/OktaLoginCallback'
import { ShiftInstructionsPage } from './ShiftInstructionsPage'
import { ShiftInstructionEditor } from 'pages/ShiftInstructionEditor'
import { PositionInstructionsPage } from './PositionInstructionsPage'
import { PositionInstructionEditor } from 'pages/PositionInstructionEditor'
import { LocationInstructionsPage } from './LocationInstructionsPage'
import { LocationInstructionEditor } from 'pages/LocationInstructionEditor'
import { AssignmentDetailPage } from './AssignmentDetailPage'
import { useGateValue } from '@statsig/react-bindings'
import { FeatureGates } from 'lib/statsig/feature_gates'
import { ScheduleView } from './AssignmentDetailPage/ScheduleContent'
import { PulsePage } from './PulsePage'
import { VndlyTimesheetPage } from './VndlyTimesheetPage'

const PageRoutes = () => {
  const location = useLocation()
  const shouldEnableLTA = useGateValue(FeatureGates.EnableLTA)
  const backgroundStack =
    (location.state && location.state.backgroundStack) || []
  return (
    <LayoutProvider>
      <ScrollRestoration />
      {backgroundStack.length > 0 ? <ModalRoutes /> : null}
      <PatchedTelemetryRoutes
        location={backgroundStack.length > 0 ? backgroundStack[0] : location}
      >
        <Route
          path={'/onboarding'}
          element={<Navigate to={'/login'} replace={true} />}
        />
        <Route path={'/login'} element={<LoginPage />} />
        <Route path={'/reset-password'} element={<ResetPasswordPage />} />
        <Route path={'/sign-out'} element={<SignOutPage />} />
        <Route path={'/invited_user/:token'} element={<OnboardingPage />} />
        <Route path={'/verify-mfa'} element={<VerifyMFA />} />
        <Route path={'/okta_callback'} element={<OktaLoginCallback />} />
        <Route
          path={'/'}
          element={
            <AuthGuard.Authenticated
              fallback={<Navigate to={'/sign-out'} replace />}
            >
              <Shell banner={<TopBanner />} />
              <Intercom />
            </AuthGuard.Authenticated>
          }
        >
          <Route index element={<Navigate to="/home" />} />
          <Route
            path={'/home/:date?'}
            element={
              <DeprecatedLayout>
                <HomePage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/home/shift-editor/:shiftId/*'}
            element={
              <DeprecatedLayout scrollsInPanel={true}>
                <ShiftEditorPage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/shift/:shiftId/instruction/:idOrCategory'}
            element={
              <DeprecatedLayout>
                <ShiftInstructionEditor />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/shift/:shiftId/instructions'}
            element={
              <DeprecatedLayout>
                <ShiftInstructionsPage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/shift/:shiftId'}
            element={
              <DeprecatedLayout>
                <ShiftDetail />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/company/:companyId/shift/:shiftId'}
            element={
              <DeprecatedLayout>
                <ShiftDetail />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/workers'}
            element={
              <DeprecatedLayout>
                <WorkersPage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/workers/:workerId/shifts'}
            element={
              <DeprecatedLayout>
                <WorkerShiftsPage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/workers/:workerId'}
            element={
              <DeprecatedLayout>
                <WorkerProfilePage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/workers/list/:rosterId'}
            element={
              <DeprecatedLayout>
                <ListDetailPage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/review'}
            element={
              <DeprecatedLayout>
                <ReviewPage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/messages/:workerId?'}
            element={
              <DeprecatedLayout>
                <MessagePage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/payments'}
            element={
              <DeprecatedLayout>
                <PaymentPage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/payments/invoice/:invoiceId'}
            element={
              <DeprecatedLayout>
                <InvoiceDetailPage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/payments/upcoming-invoice'}
            element={
              <DeprecatedLayout>
                <UpcomingInvoiceDetailPage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/analytics'}
            element={
              <DeprecatedLayout scrollsInPanel={true}>
                <AnalyticsPage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/pulse/:tab?'}
            element={
              <DeprecatedLayout>
                <PulsePage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/vndly'}
            element={<Navigate to={'/vndly/timesheet'} replace />}
          />
          <Route
            path={'/vndly/timesheet'}
            element={
              <DeprecatedLayout>
                <VndlyTimesheetPage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/settings/:tab?'}
            element={
              <DeprecatedLayout>
                <SettingsPage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/profile/tutorials'}
            element={<Navigate to={'/settings/tutorials'} replace={true} />}
          />
          <Route
            path={'/position/:positionId/instruction/:idOrCategory'}
            element={
              <DeprecatedLayout>
                <PositionInstructionEditor />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/position/:positionId/instructions'}
            element={
              <DeprecatedLayout>
                <PositionInstructionsPage />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/location/:locationId/instruction/:idOrCategory'}
            element={
              <DeprecatedLayout>
                <LocationInstructionEditor />
              </DeprecatedLayout>
            }
          />
          <Route
            path={'/location/:locationId/instructions'}
            element={
              <DeprecatedLayout>
                <LocationInstructionsPage />
              </DeprecatedLayout>
            }
          />
          {shouldEnableLTA ? (
            <Route
              path={'/assignments/:assignmentId'}
              element={
                <DeprecatedLayout>
                  <AssignmentDetailPage />
                </DeprecatedLayout>
              }
            >
              <Route index element={<Navigate to="calendar" replace />} />
              <Route path="calendar" element={<ScheduleView />} />
              <Route path="list" element={<ScheduleView />} />
            </Route>
          ) : null}
          <Route
            path={'*'}
            element={
              <DeprecatedLayout>
                <NotFound />
              </DeprecatedLayout>
            }
          />
        </Route>
      </PatchedTelemetryRoutes>
    </LayoutProvider>
  )
}

const router = createBrowserRouter(
  createRoutesFromElements(<Route path={'*'} Component={PageRoutes} />)
)

export default function Pages() {
  return (
    <RouterProvider router={router} future={{ v7_startTransition: true }} />
  )
}
